import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { encrypttoHEX } from "../routes/encodedecode.js";

const baseURL = `${window.location.protocol}//${window.location.hostname}${window.location.port ? ":" + window.location.port : ""}/`;

const PromptCreate = () => {
  const location = useLocation();
  const [organizations, setOrganizations] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [categories, setCategories] = useState([]); // ADD THIS
  const [organizationId, setOrganizationId] = useState(
    location.state?.organizationId || "",
  );
  const [cohortId, setCohortId] = useState(location.state?.cohortId || "");
  const [promptText, setPromptText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [message, setMessage] = useState("");
  const [defaultCategory, setDefaultCategory] = useState(""); // CHANGE TO EMPTY STRING
  const [sessionLink, setSessionLink] = useState("");

  function getLocalDateFromInput(dateStr, endOfDay = false) {
    const [year, month, day] = dateStr.split("-").map(Number);
    if (endOfDay) {
      return new Date(year, month - 1, day, 23, 59, 59);
    } else {
      return new Date(year, month - 1, day, 0, 0, 0);
    }
  }

  const [minDate, setMinDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  });

  useEffect(() => {
    const fetchOrganizations = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await axios.get(
          "https://genz-staging.feeltiptop.com/api/social_glass/organizations",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setOrganizations(response.data);
      } catch (error) {
        console.error(
          "Error fetching organizations:",
          error.response?.data?.error,
        );
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchCohorts = async () => {
      const token = localStorage.getItem("token");
      if (organizationId) {
        try {
          const response = await axios.get(
            `https://genz-staging.feeltiptop.com/api/social_glass/cohorts?organization_id=${organizationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          setCohorts(response.data);
        } catch (error) {
          console.error("Error fetching cohorts:", error.response?.data?.error);
        }
      } else {
        setCohorts([]);
      }
    };
    fetchCohorts();
  }, [organizationId]);

  // ADD THIS NEW useEffect to fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await axios.get(
          "https://genz-staging.feeltiptop.com/api/social_glass/categories",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setCategories(response.data);
        
        // Set first category as default, or find "General, Trends, & Humor"
        if (response.data.length > 0) {
          const generalCategory = response.data.find(cat => 
            cat.CategoryName === "General, Trends, & Humor"
          );
          setDefaultCategory(generalCategory ? generalCategory.CategoryName : response.data[0].CategoryName);
        }
      } catch (error) {
        console.error("Error fetching categories:", error.response?.data?.error);
        // Fallback to hardcoded default if API fails
        setDefaultCategory("General, Trends, & Humor");
      }
    };

    fetchCategories();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!organizationId || !cohortId || !promptText || !startDate || !endDate || !defaultCategory) {
      setMessage("All fields are required.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setMessage("Authentication token not found. Please log in again.");
      return;
    }

    const startDateObj = getLocalDateFromInput(startDate);
    const endDateObj = getLocalDateFromInput(endDate, true);

    const now = new Date();
    const currentDateLocal = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    );

    if (startDateObj < currentDateLocal) {
      alert("Start date cannot be before the current date.");
      return;
    }

    if (startDateObj > endDateObj) {
      alert("Start date cannot be later than end date.");
      return;
    }

    if (endDateObj < currentDateLocal) {
      alert("End date cannot be before the current date.");
      return;
    }

    try {
      const response = await axios.post(
        "https://genz-staging.feeltiptop.com/api/social_glass/prompt",
        {
          organization_id: organizationId,
          cohort_id: cohortId,
          prompt_text: promptText,
          start_date: startDate,
          end_date: endDate,
          base_url: baseURL,
          default_category: defaultCategory
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      console.log("Response:", response.data);

      setMessage(response.data.message);
      setPromptText("");
      setStartDate("");
      setEndDate("");
    } catch (error) {
      setMessage(error.response?.data?.error || "Error creating prompt");
      console.error("Prompt creation failed:", error);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-4xl overflow-hidden rounded-xl bg-white shadow-lg">
        <div className="px-8 py-8 sm:p-10">
          <h2 className="mb-8 text-center text-3xl font-bold text-gray-900">
            Create Prompt
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="form-group">
              <label
                htmlFor="organization"
                className="mb-2 block text-base font-medium text-gray-700"
              >
                Organization
              </label>
              <select
                id="organization"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                required
                className="block w-full rounded-md border-gray-300 bg-white px-4 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Organization</option>
                {organizations.map((org) => (
                  <option key={org.OrganizationID} value={org.OrganizationID}>
                    {org.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label
                htmlFor="cohort"
                className="mb-2 block text-base font-medium text-gray-700"
              >
                Cohort
              </label>
              <select
                id="cohort"
                value={cohortId}
                onChange={(e) => setCohortId(e.target.value)}
                required
                className="block w-full rounded-md border-gray-300 bg-white px-4 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={!organizationId}
              >
                <option value="">Select Cohort</option>
                {cohorts.map((cohort) => (
                  <option key={cohort.CohortID} value={cohort.CohortID}>
                    {cohort.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label
                htmlFor="promptText"
                className="mb-2 block text-base font-medium text-gray-700"
              >
                Prompt Text
              </label>
              <textarea
                id="promptText"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                required
                rows={5}
                className="block w-full rounded-md border-gray-300 bg-white px-4 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter prompt text"
              />
            </div>

            <div className="form-group">
              <label
                htmlFor="defaultCategory"
                className="mb-2 block text-base font-medium text-gray-700"
              >
                Default Category
              </label>
              <select
                id="defaultCategory"
                value={defaultCategory}
                onChange={(e) => setDefaultCategory(e.target.value)}
                required
                className="block w-full rounded-md border-gray-300 bg-white px-4 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a category...</option>
                {categories.map((category) => (
                  <option key={category.CategoryID} value={category.CategoryName}>
                    {category.CategoryName}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="form-group">
                <label
                  htmlFor="startDate"
                  className="mb-2 block text-base font-medium text-gray-700"
                >
                  Start Date
                </label>
                <input
                  id="startDate"
                  type="date"
                  min={minDate}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block w-full rounded-md border-gray-300 bg-white px-4 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="form-group">
                <label
                  htmlFor="endDate"
                  className="mb-2 block text-base font-medium text-gray-700"
                >
                  End Date
                </label>
                <input
                  id="endDate"
                  type="date"
                  min={minDate}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block w-full rounded-md border-gray-300 bg-white px-4 py-3 text-base shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-6 py-3 text-lg font-medium text-white shadow-sm transition duration-150 ease-in-out hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Create Prompt
              </button>
            </div>
          </form>

          {message && (
            <div
              className={`mt-6 rounded-md p-4 text-base ${message.includes("Error") ? "border border-red-200 bg-red-50 text-red-700" : "border border-green-200 bg-green-50 text-green-700"}`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromptCreate;
